/**
 * Phone features behind one small surface, so pages don't care where they run:
 *  - Android app (Capacitor, built with `vite build --mode app`): native GPS, background tracking
 *    with a persistent notification, the camera, the refresh token kept on the device.
 *  - Browser: navigator.geolocation (only while the page is open and the screen is on), a file
 *    input for photos, cookies for the session.
 * The Capacitor plugins are only imported in the app build, so the web portal doesn't carry them.
 */
import type { Fix, FieldPoint } from "./api/types";

declare const __APP__: boolean;
export const isApp: boolean = typeof __APP__ !== "undefined" && __APP__;

type Plugins = {
  Geolocation: typeof import("@capacitor/geolocation").Geolocation;
  Camera: typeof import("@capacitor/camera").Camera;
  Preferences: typeof import("@capacitor/preferences").Preferences;
  Device: typeof import("@capacitor/device").Device;
  Network: typeof import("@capacitor/network").Network;
  LocalNotifications: typeof import("@capacitor/local-notifications").LocalNotifications;
  App: typeof import("@capacitor/app").App;
  BG: any;
};
let plugins: Promise<Plugins> | null = null;
function native(): Promise<Plugins> {
  if (!isApp) return Promise.reject(new Error("not the app"));
  plugins ??= (async () => {
    const [{ Geolocation }, { Camera }, { Preferences }, { Device }, { Network }, { LocalNotifications }, { App }, { registerPlugin }] = await Promise.all([
      import("@capacitor/geolocation"), import("@capacitor/camera"), import("@capacitor/preferences"), import("@capacitor/device"),
      import("@capacitor/network"), import("@capacitor/local-notifications"), import("@capacitor/app"), import("@capacitor/core")]);
    return { Geolocation, Camera, Preferences, Device, Network, LocalNotifications, App, BG: registerPlugin("BackgroundGeolocation") };
  })();
  return plugins;
}

export class LocationError extends Error { constructor(public code: "DENIED" | "UNAVAILABLE" | "TIMEOUT", message: string) { super(message); } }

/** One fresh, accurate position (asks for permission the first time). */
export async function currentFix(): Promise<Fix> {
  if (isApp) {
    const { Geolocation } = await native();
    const perm = await Geolocation.checkPermissions().catch(() => null);
    if (perm?.location !== "granted") { const r = await Geolocation.requestPermissions({ permissions: ["location"] }).catch(() => null); if (r?.location !== "granted") throw new LocationError("DENIED", "Location permission was refused."); }
    try { const p = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 25_000, maximumAge: 0 }); return { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, at: new Date(p.timestamp).toISOString() }; }
    catch (e: any) { throw new LocationError(/denied|permission/i.test(String(e?.message)) ? "DENIED" : /timeout/i.test(String(e?.message)) ? "TIMEOUT" : "UNAVAILABLE", String(e?.message ?? e)); }
  }
  if (!("geolocation" in navigator)) throw new LocationError("UNAVAILABLE", "This browser can't give a location.");
  return new Promise((res, rej) => navigator.geolocation.getCurrentPosition(
    (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, at: new Date(p.timestamp).toISOString() }),
    (e) => rej(new LocationError(e.code === 1 ? "DENIED" : e.code === 3 ? "TIMEOUT" : "UNAVAILABLE", e.message)),
    { enableHighAccuracy: true, timeout: 25_000, maximumAge: 0 }));
}

export async function batteryPct(): Promise<number | null> {
  try {
    if (isApp) { const b = await (await native()).Device.getBatteryInfo(); return b.batteryLevel != null ? Math.round(b.batteryLevel * 100) : null; }
    const b = await (navigator as any).getBattery?.(); return b ? Math.round(b.level * 100) : null;
  } catch { return null; }
}

/**
 * Keep receiving positions while the field day is open. In the app this keeps working with the
 * screen off (Android shows a notification while it runs). In a browser it stops when the page
 * is closed or the phone is locked. Calls `onPoint` at most once per `intervalSec`.
 */
export async function startTracking(o: { intervalSec: number; title: string; message: string; onPoint: (p: FieldPoint) => void; onError: (e: LocationError) => void }): Promise<() => Promise<void>> {
  let last = 0;
  const take = async (lat: number, lng: number, accuracy: number | null, time: number, speed: number | null) => {
    if (time - last < o.intervalSec * 1000 - 2000) return; last = time;
    o.onPoint({ at: new Date(time).toISOString(), lat, lng, accuracy, speed: speed != null && speed >= 0 ? speed : null, battery: await batteryPct() });
  };
  if (isApp) {
    const { BG, LocalNotifications } = await native();
    // Android 13+: the "location in use" notification needs this permission.
    await LocalNotifications.requestPermissions().catch(() => null);
    const id: string = await BG.addWatcher({ backgroundTitle: o.title, backgroundMessage: o.message, requestPermissions: true, stale: false,
      // 0 = updates also while standing still (a shop visit must show as a stop, and "last seen" stays fresh).
      // GPS is on anyway while the watcher runs; only one point per intervalSec is kept and sent.
      distanceFilter: 0 },
      (loc: any, err: any) => {
        if (err) { o.onError(new LocationError(err.code === "NOT_AUTHORIZED" ? "DENIED" : "UNAVAILABLE", err.message ?? String(err))); return; }
        if (loc) take(loc.latitude, loc.longitude, loc.accuracy ?? null, loc.time ?? Date.now(), loc.speed ?? null);
      });
    return async () => { await BG.removeWatcher({ id }).catch(() => {}); };
  }
  if (!("geolocation" in navigator)) { o.onError(new LocationError("UNAVAILABLE", "This browser can't give a location.")); return async () => {}; }
  const w = navigator.geolocation.watchPosition((p) => take(p.coords.latitude, p.coords.longitude, p.coords.accuracy, p.timestamp, p.coords.speed),
    (e) => o.onError(new LocationError(e.code === 1 ? "DENIED" : e.code === 3 ? "TIMEOUT" : "UNAVAILABLE", e.message)), { enableHighAccuracy: true, maximumAge: 15_000 });
  return async () => navigator.geolocation.clearWatch(w);
}

/** Opens the phone's app settings (after the person refused location). App only. */
export async function openAppSettings() { if (isApp) await (await native()).BG.openSettings().catch(() => {}); }

/** Camera only (not the gallery) in the app, so the photo is taken at the visit. Returns a JPEG data URL. */
export async function takePhotoNative(): Promise<string | null> {
  const { Camera } = await native();
  const { CameraResultType, CameraSource } = await import("@capacitor/camera");
  try {
    const p = await Camera.getPhoto({ source: CameraSource.Camera, resultType: CameraResultType.DataUrl, quality: 60, width: 1280, correctOrientation: true, saveToGallery: false });
    return p.dataUrl ? await shrinkDataUrl(p.dataUrl) : null;
  } catch { return null; } // cancelled
}

/** Resize to at most 1280 px and re-encode as JPEG (~100–250 KB), for a picked or captured file. */
export async function fileToJpeg(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try { return await shrinkDataUrl(url); } finally { URL.revokeObjectURL(url); }
}
async function shrinkDataUrl(src: string, max = 1280, quality = 0.7): Promise<string> {
  const img = new Image(); img.src = src; await img.decode();
  const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement("canvas"); c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
  c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL("image/jpeg", quality);
}

/** Network back online (app: native event; browser: the online event). */
export async function onOnline(cb: () => void): Promise<() => void> {
  if (isApp) { const h = await (await native()).Network.addListener("networkStatusChange", (s) => s.connected && cb()); return () => { h.remove(); }; }
  addEventListener("online", cb); return () => removeEventListener("online", cb);
}
export async function onResume(cb: () => void): Promise<void> { if (isApp) await (await native()).App.addListener("resume", cb); else document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && cb()); }

/** Where the app keeps the refresh token (Capacitor Preferences = Android SharedPreferences, private to the app). */
export const appTokenStore = {
  async get() { try { return (await (await native()).Preferences.get({ key: "kormi.rt" })).value; } catch { return null; } },
  async set(v: string | null) { const { Preferences } = await native(); if (v) await Preferences.set({ key: "kormi.rt", value: v }); else await Preferences.remove({ key: "kormi.rt" }); },
};
